// The challenge ladder (#66): a map's goals in tiers (THPS 3/4 style). Tier 1
// is open from the start; a higher-tier goal stays locked until the goals it
// `requires` are done; some goals pay stat points (#35), more on higher tiers.
// Sim-free and three.js-free: Game asks it what's locked and what a goal
// pays; the markers, the prompt and Pause > Challenges show it.
//
// The ladder comes from the map file's `challenges` list: one entry per goal
// (score, gap and collect goals, by goal id) with its tier, what it requires
// and what it pays. `parseMapFile` checks the list (`checkChallenges`). An
// entry with a `kind` is a task instead (a named trick, a big combo: see
// tasks.ts), with that kind's fields.

import type { TaskGoal } from "./tasks";

export type Tier = 1 | 2 | 3;

export const TIER_NAMES = ["Warm up", "Getting good", "Pro"] as const;

/** Most stat points one challenge pays. */
export const MAX_RUNG_POINTS = 2;

/** One goal's place on the ladder. */
export interface Rung {
  /** The goal id, as in `Run.goals` ("score-0", "gap-Rail Hop", "skate"...). */
  id: string;
  tier: Tier;
  /** Goal ids that must be done before this one unlocks. */
  requires: string[];
  /** Stat points paid the first time it's done (0-2). */
  statPoints: number;
}

/** A map file's `challenges` entry: a rung, with `requires` optional (none), and a task's `kind` and fields if it's a task. */
export type MapChallenge = Omit<Rung, "requires"> & { requires?: string[] } & Partial<Omit<TaskGoal, "id">>;

/**
 * The ladder for a map: its `challenges` list in goal-list order (`goalIds`,
 * see `goalIds` in run.ts). A map file without a list (one loaded in dev)
 * gets a flat ladder: everything open on tier 1, paying a point.
 */
export function mapLadder(challenges: readonly MapChallenge[] | undefined, goalIds: readonly string[]): Rung[] {
  if (!challenges) return goalIds.map((id) => ({ id, tier: 1, requires: [], statPoints: 1 }));
  const byId = new Map(challenges.map((c) => [c.id, c]));
  return goalIds.flatMap((id) => {
    const c = byId.get(id);
    return c ? [{ id, tier: c.tier, requires: [...(c.requires ?? [])], statPoints: c.statPoints }] : [];
  });
}

/** Stat points the whole ladder pays. */
export function ladderPoints(rungs: readonly Rung[]): number {
  return rungs.reduce((sum, r) => sum + r.statPoints, 0);
}

/**
 * What's wrong with a ladder, one line each: a requirement that isn't on it,
 * or a rung that never unlocks however much you do (a cycle, or behind one).
 */
export function checkLadder(rungs: readonly Rung[]): string[] {
  const problems: string[] = [];
  const ids = new Set(rungs.map((r) => r.id));
  for (const r of rungs) for (const need of r.requires) if (!ids.has(need)) problems.push(`${r.id}: requires "${need}", which isn't on the ladder`);
  // Do everything that's open, over and over, until nothing new opens.
  const done = new Set<string>();
  for (let grew = true; grew; ) {
    grew = false;
    for (const r of rungs) {
      if (done.has(r.id) || !r.requires.every((need) => done.has(need) || !ids.has(need))) continue;
      done.add(r.id);
      grew = true;
    }
  }
  const stuck = rungs.filter((r) => !done.has(r.id)).map((r) => `${r.id}: never unlocks (a cycle, or something it needs never does)`);
  return [...stuck, ...problems];
}

/** A map's ladder against the session's done goals. */
export class LadderState {
  private byId: Map<string, Rung>;

  constructor(
    readonly rungs: readonly Rung[],
    private done: (id: string) => boolean,
  ) {
    this.byId = new Map(rungs.map((r) => [r.id, r]));
  }

  rung(id: string): Rung | undefined {
    return this.byId.get(id);
  }

  /** Goal ids this one still waits on. */
  missing(id: string): string[] {
    return this.byId.get(id)?.requires.filter((need) => !this.done(need)) ?? [];
  }

  /** Locked until everything it requires is done. Goals not on the ladder are never locked. */
  locked(id: string): boolean {
    return this.missing(id).length > 0;
  }

  /** Stat points it pays the first time. */
  reward(id: string): number {
    return this.byId.get(id)?.statPoints ?? 0;
  }
}
